import { Dispatch, MouseEvent, RefObject, useRef, useState } from 'react';
import {
  AlertColor,
  Button,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import strings from 'common/Translation/Translate';
import Form, { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import { VarianceChip, VarianceQtyHint } from 'components/InputField';
import { AddEditOtherItemsForm } from './AddEditOtherItemsForm';
import { ActualizationTableEmpty } from '../ActualizationTableEmpty';
import { ActualizationTaxCell } from '../ActualizationTaxCell';
import {
  actualizationTableSxFor,
  lineItemColumnWidths,
  mobileLineItemColumnWidths,
  useActualizationMobile,
} from '../actualizationTableLayout';
import DrawerMenu from 'components/DrawerMenu';
import { removeErrorFieldsFromValues } from 'utils/validators';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Tabular from 'components/Tabular/Tabular';
import { FormComponent } from 'utils/validators/HelperComponents';
import { formatCurrency, getDrawerWidth } from 'utils/validators/HelperFunctions';
import { ToolTip } from 'components/Tooltip/ToolTip';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { selectIntegration } from 'utils/redux/reducer/authentication-slice';

type TableComponentProps = {
  editedData: any;
  editRow: (idx: any) => void;
  deleteRow: (idx: any) => void;
  noGuest?: any;
  pastBooking: boolean;
  dropdownData: { label: string; value: string }[];
  addOnCategories: any;
  isAng: boolean;
  previewItems?: any[];
  previewVariances?: any[];
};

const qtyNumber = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const otherItemLocalAmount = (row: any, noGuest: any) => {
  const qty = row.type === 'PER_QUANTITY' ? qtyNumber(row.quantity) : qtyNumber(noGuest);
  const rate = qtyNumber(row.price);
  const gross = rate * qty;
  const discount = qtyNumber(row.discount);
  let net = gross;
  if (discount && row.discountType) {
    net =
      row.discountType === 'PERCENT' ? Math.max(0, gross - (gross * discount) / 100) : Math.max(0, gross - discount);
  }
  const tax = row.itemTax == null || row.itemTax === '' ? 0 : qtyNumber(row.itemTax);
  const serviceCharge =
    row.itemServiceCharge == null || row.itemServiceCharge === '' ? 0 : qtyNumber(row.itemServiceCharge);
  return net + serviceCharge + tax;
};

const rateLabel = (row: any) => {
  const formatted = formatCurrency(qtyNumber(row.price));
  const template =
    row.type === 'PER_QUANTITY' ? strings.actualization_addon_rate_unit : strings.actualization_addon_rate_person;
  return String(strings.formatString(template, formatted));
};

const discountLabel = (row: any) => {
  if (row.discount == null || row.discount === '' || Number(row.discount) === 0) return '—';
  return row.discountType === 'PERCENT' ? `${row.discount}%` : formatCurrency(qtyNumber(row.discount));
};

const categoryLabel = (row: any, addOnCategories: any, previewName?: string) =>
  previewName ??
  addOnCategories?.find((opt: any) => String(opt.value) === String(row?.category?.value ?? row?.category))?.label ??
  row?.categoryName ??
  row?.category?.label ??
  (typeof row.category === 'string' ? row.category : null) ??
  '—';

const taxGroupLabel = (row: any, dropdownData: { label: string; value: string }[], previewName?: string) =>
  previewName ??
  dropdownData?.find((opt: any) => String(opt.value) === String(row?.taxGroupId?.value ?? row?.taxGroupId))?.label ??
  row?.selectedTaxGroup?.label ??
  row?.taxGroupId?.label ??
  '—';

const varianceTone = (delta: number) => (delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral');

const AmountVarianceCell = ({ deltaAmount, deltaQty }: { deltaAmount: number; deltaQty: number }) => (
  <Stack alignItems="flex-end" spacing={0.25}>
    <VarianceChip label={formatCurrency(Math.abs(deltaAmount))} tone={varianceTone(deltaAmount)} />
    <VarianceQtyHint delta={deltaQty} />
  </Stack>
);

const TableComponent = ({
  editedData,
  editRow,
  deleteRow,
  noGuest,
  pastBooking,
  dropdownData,
  addOnCategories,
  isAng,
  previewItems,
  previewVariances,
}: TableComponentProps) => {
  const mobile = useActualizationMobile();
  const headers = [
    strings.description,
    strings.actualization_addon_qty,
    ...(mobile ? [] : [strings.actualization_addon_pricing, strings.actualization_addon_discount]),
    ...(mobile || isAng ? [] : [strings.actualization_addon_tax]),
    strings.actualization_addon_amount,
    ...(mobile ? [] : [strings.actualization_addon_variance]),
    ...(pastBooking ? [''] : []),
  ];
  const columnAlign = headers.map((_, i) => (i === 0 ? 'left' : 'right')) as Array<'left' | 'right'>;
  const totalAmount = editedData.reduce((sum: number, row: any, idx: number) => {
    const previewAmount = previewItems?.[idx]?.amount;
    return sum + (previewAmount == null ? qtyNumber(otherItemLocalAmount(row, noGuest)) : qtyNumber(previewAmount));
  }, 0);
  const amountColIndex = mobile ? 2 : isAng ? 4 : 5;

  return (
    <Tabular
      size="small"
      headerColor="secondary"
      rows={headers}
      columnAlign={columnAlign}
      columnWidths={mobile ? mobileLineItemColumnWidths(pastBooking) : lineItemColumnWidths(pastBooking, !isAng)}
      sx={actualizationTableSxFor(mobile)}>
      {editedData.map((row: any, index: number) => {
        const previewLine = previewItems?.[index];
        const previewVariance = previewVariances?.[index];
        const amount = previewLine?.amount == null ? otherItemLocalAmount(row, noGuest) : previewLine.amount;
        const qty = row.type === 'PER_QUANTITY' ? qtyNumber(row.quantity) : qtyNumber(noGuest);
        const category = categoryLabel(row, addOnCategories, previewLine?.categoryName);
        const rowTaxGroupId = row?.taxGroupId?.value ?? row?.taxGroupId ?? '';
        const previewTaxMatches =
          previewLine == null || String(previewLine.taxGroupId ?? '') === String(rowTaxGroupId);
        return (
          <TableRow
            key={row.id ?? `${row.name}-${index}`}
            hover={pastBooking}
            onClick={pastBooking ? () => editRow(index) : undefined}
            sx={
              pastBooking
                ? {
                    cursor: 'pointer',
                    '&:hover .actualization-delete-btn': { color: 'error.main' },
                  }
                : undefined
            }>
            <TableCell scope="row">
              <Stack spacing={0.125}>
                <Typography sx={{ fontSize: '13px', lineHeight: 1.25, fontWeight: 600 }}>{row.name || '—'}</Typography>
                {category && category !== '—' ? (
                  <Typography color="text.secondary" sx={{ fontSize: '11px', lineHeight: 1.25 }}>
                    {category}
                  </Typography>
                ) : null}
              </Stack>
            </TableCell>
            <TableCell align="right">
              <Typography sx={{ fontSize: '14px', lineHeight: 1.25, fontWeight: 500 }}>{qty}</Typography>
            </TableCell>
            {!mobile && (
              <>
                <TableCell align="right">{rateLabel(row)}</TableCell>
                <TableCell align="right">{discountLabel(row)}</TableCell>
                {!isAng && (
                  <TableCell align="right">
                    <ActualizationTaxCell
                      amount={previewTaxMatches ? (previewLine?.itemTax ?? row.itemTax) : null}
                      taxGroupName={taxGroupLabel(
                        row,
                        dropdownData,
                        previewTaxMatches ? previewLine?.taxGroupName : undefined,
                      )}
                    />
                  </TableCell>
                )}
              </>
            )}
            <TableCell align="right">{formatCurrency(amount)}</TableCell>
            {!mobile && (
              <TableCell align="right">
                {previewVariance ? (
                  <AmountVarianceCell
                    deltaAmount={qtyNumber(previewVariance.deltaAmount)}
                    deltaQty={qtyNumber(previewVariance.deltaQuantity)}
                  />
                ) : isAng ? (
                  <AmountVarianceCell deltaAmount={qtyNumber(amount)} deltaQty={qty} />
                ) : (
                  '—'
                )}
              </TableCell>
            )}
            {pastBooking && (
              <TableCell align="right">
                <ToolTip title="Edit">
                  <IconButton
                    size="small"
                    sx={{ mr: 0.5, p: 0.25 }}
                    onClick={e => {
                      e.stopPropagation();
                      editRow(index);
                    }}
                    aria-label="edit"
                    color="primary">
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ToolTip>
                <ToolTip title="Delete">
                  <IconButton
                    size="small"
                    className="actualization-delete-btn"
                    sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}
                    onClick={e => {
                      e.stopPropagation();
                      deleteRow(index);
                    }}
                    aria-label="delete"
                    color="primary">
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ToolTip>
              </TableCell>
            )}
          </TableRow>
        );
      })}
      <TableRow
        sx={{
          '& td': {
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: 1.25,
            py: 0.75,
            px: 1,
            boxShadow: theme => `inset 0 1px 0 ${theme.palette.divider}`,
          },
        }}>
        <TableCell colSpan={amountColIndex}>
          {isAng ? strings.actualization_ang_total : strings.actualization_other_total}
        </TableCell>
        <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
        {!mobile && <TableCell align="right" />}
        {pastBooking && <TableCell align="right" />}
      </TableRow>
    </Tabular>
  );
};

type AddEditOtherItemsProps = {
  editedData: any;
  setData: Dispatch<any>;
  noGuest?: any;
  pastBooking: boolean;
  dropdownData: { label: string; value: string }[];
  addOnCategories: any;
  isAng: boolean;
  previewItems?: any[];
  previewVariances?: any[];
};

export const AddEditOtherItems: React.FC<AddEditOtherItemsProps> = ({
  editedData = [],
  setData,
  noGuest,
  pastBooking,
  dropdownData,
  addOnCategories,
  isAng,
  previewItems,
  previewVariances,
}) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const integration = useSelector(selectIntegration);
  const isOperaIntegration = Boolean(integration && integration.includes('OPERA'));

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [isPerGuest, setIsPerGuest] = useState<boolean>(false);
  const [pageResponse, setPageResponse] = useState<any>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [discountDisabled, setDiscountDisabled] = useState<boolean>(true);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  let formRef: RefObject<Form | null | undefined> = useRef();

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setPageResponse({});
    formRef.current?.resetForm();
    setSelectedRowId(null);
    setHasError(false);
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData);
    data.quantity = isPerGuest ? data.quantity : noGuest;
    setHasError(false);
    if (isFormValid) {
      if (data.discountType === 'FLAT' && String(data.discount ?? '').trim().length > 10) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.flatDiscountError,
        });
      } else if (data.discountType === 'PERCENT' && parseInt(formData.discount) > 100) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.percentDiscountError,
        });
      } else {
        const array = [...editedData];
        const taxId = data.taxGroupId?.value ?? data.taxGroupId ?? null;
        const selectedTax = dropdownData.find((opt: any) => String(opt.value) === String(taxId));
        const saved = {
          ...data,
          taxGroupId: taxId ?? '',
          selectedTaxGroup: selectedTax ? { value: selectedTax.value, label: selectedTax.label } : null,
        };
        if (selectedRowId !== null) {
          array[selectedRowId] = { ...array[selectedRowId], ...saved };
          setSelectedRowId(null);
        } else {
          array.push(saved);
        }
        setData(array);
        setOpenDrawer(false);
        setPageResponse({});
      }
    } else {
      setHasError(true);
    }
  };

  const deleteRow = (idx: any) => {
    const deleteRow = JSON.parse(JSON.stringify(editedData));
    const deleteValue = deleteRow.filter((item: any, i: any) => i !== idx);
    setData(deleteValue);
  };

  const editRow = (idx: any) => {
    setSelectedRowId(idx);
    const editRow = JSON.parse(JSON.stringify(editedData));
    const editValue = editRow.find((item: any, i: any) => i === idx);
    setIsPerGuest(editValue.type === 'PER_QUANTITY' ? true : false);
    setPageResponse(editValue);
    setOpenDrawer(true);
  };
  const handleChange = (field: string, value: string, formData: any) => {
    if (field === 'type') {
      setIsPerGuest(value === 'PER_QUANTITY' ? true : false);
      setPageResponse({
        ...formData,
        id: pageResponse.id,
        type: value,
      });
    } else if (field === 'category') {
      const selectedCategory = addOnCategories.find((cat: any) => cat.value === value);
      const hsnCode = selectedCategory?.hsnCode || '';
      setPageResponse({
        ...formData,
        id: pageResponse.id,
        category: value,
        hsnCode: hsnCode,
      });
    } else if (field === 'discountType') {
      if (value === '') setDiscountDisabled(true);
      else {
        setDiscountDisabled(false);
      }
    }
  };
  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {(pastBooking || editedData?.length > 0) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 0.5, mb: 1.5, minHeight: 36, gap: 1 }}>
          <Typography color="primary" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }}>
            {isAng ? strings.actualization_section_ang : strings.actualization_section_other}
          </Typography>
          {pastBooking && (
            <Button
              onClick={() => setOpenDrawer(true)}
              variant="outlined"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              sx={{ minHeight: 36 }}>
              {isAng ? strings.actualization_section_ang_add : strings.actualization_section_other_add}
            </Button>
          )}
        </Stack>
      )}
      {editedData?.length ? (
        <TableComponent
          isAng={isAng}
          pastBooking={pastBooking}
          noGuest={noGuest}
          editedData={editedData}
          editRow={editRow}
          deleteRow={deleteRow}
          dropdownData={dropdownData}
          addOnCategories={addOnCategories}
          previewItems={previewItems}
          previewVariances={previewVariances}
        />
      ) : pastBooking ? (
        <ActualizationTableEmpty
          message={isAng ? strings.actualization_section_ang_empty : strings.actualization_section_other_empty}
        />
      ) : null}
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={isAng ? strings.angTitle : strings.otherItems}>
        <FormComponent
          isFormUpdated={isPerGuest}
          hasError={hasError}
          formRef={formRef}
          handleChange={handleChange}
          pageResponse={pageResponse}
          handleSave={handleSave}
          formModel={AddEditOtherItemsForm(
            strings,
            isPerGuest,
            dropdownData,
            discountDisabled,
            addOnCategories,
            isAng,
            isOperaIntegration,
          )}
        />
      </DrawerMenu>
    </>
  );
};
